import { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash, CircleNotch, FileText, ArrowRight, CaretDown } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const STATUS_COLORS = {
  Draft: { c: "var(--bk-text-2)", b: "var(--bk-page)" },
  Sent: { c: "#B5761F", b: "#FBF3E2" },
  Accepted: { c: "#16A34A", b: "var(--bk-success-bg)" },
  Declined: { c: "#B23B1F", b: "#FDECE8" },
  Converted: { c: "var(--bk-accent)", b: "var(--bk-success-bg)" },
};

const blankItem = () => ({ description: "", quantity: 1, rate: 0 });

export function Estimates({ businessId }) {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", date: today(), expiry_date: "", notes: "", items: [blankItem()] });

  const load = () => api.get(`/businesses/${businessId}/estimates`).then((r) => { setEstimates(r.data.estimates); setStatuses(r.data.statuses); }).catch(() => {});
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.rate) || 0), 0);
  const setItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));

  const create = async () => {
    if (!form.client_name.trim()) return toast.error("Add a client name");
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/estimates`, {
        ...form, items: form.items.filter((i) => i.description.trim()).map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, rate: Number(i.rate) || 0 })),
      });
      toast.success("Estimate created");
      setForm({ client_name: "", client_email: "", date: today(), expiry_date: "", notes: "", items: [blankItem()] });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create the estimate.");
    } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/businesses/${businessId}/estimates/${id}/status`, { status }); load(); } catch { toast.error("Couldn't update status"); }
  };
  const del = async (id) => { try { await api.delete(`/businesses/${businessId}/estimates/${id}`); load(); } catch {} };
  const convert = async (id) => {
    try {
      const { data } = await api.post(`/businesses/${businessId}/estimates/${id}/convert`);
      toast.success(`Converted to invoice ${data.invoice.number}`, { action: { label: "Open invoices", onClick: () => navigate(`/business/${businessId}?tab=invoices`) } });
      load();
      navigate(`/business/${businessId}?tab=invoices`);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't convert."); }
  };

  return (
    <div className="space-y-6" data-testid="estimates-tab">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}><RowanTerm term="estimate">Estimates & quotes</RowanTerm></h3>
        <button onClick={() => setShowForm((s) => !s)} data-testid="new-estimate-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold">
          <Plus size={16} weight="bold" /> New estimate
        </button>
      </div>

      {showForm && (
        <div className="bk-card p-5 space-y-3" data-testid="estimate-builder-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Client name" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} data-testid="est-client-name" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Client email (optional)" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <label className="text-xs flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}>Date <DateInput value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="est-date" className="flex-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} /></label>
            <label className="text-xs flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}>Expires <DateInput value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} data-testid="est-expiry" className="flex-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} /></label>
          </div>
          <div className="space-y-2">
            {form.items.map((it, idx) => (
              <div key={idx} className="flex gap-2 items-center" data-testid={`est-item-${idx}`}>
                <input placeholder="Description" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} className="flex-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="w-16 rounded-md border px-2 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input type="number" placeholder="Rate" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} className="w-24 rounded-md border px-2 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <span className="w-24 text-right text-sm tabular-nums" style={{ color: "var(--bk-text-2)" }}>{money((Number(it.quantity) || 0) * (Number(it.rate) || 0))}</span>
                <button onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="p-1.5"><Trash size={14} color="#B23B1F" /></button>
              </div>
            ))}
            <button onClick={() => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }))} data-testid="add-est-line" className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: "var(--bk-accent)" }}><Plus size={14} weight="bold" /> Add line</button>
          </div>
          <textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <div className="flex items-center justify-between">
            <span className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Total: {money(total)}</span>
            <button onClick={create} disabled={saving} data-testid="save-estimate-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold disabled:opacity-70">
              {saving ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : "Save estimate"}
            </button>
          </div>
        </div>
      )}

      {estimates.length === 0 ? (
        <div className="bk-card p-8 text-center" style={{ color: "var(--bk-text-2)" }}>
          <FileText size={30} weight="duotone" className="mx-auto mb-2" color="var(--bk-accent)" />
          <p className="text-sm">No estimates yet. Create a quote and convert it to an invoice when the client says yes.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {estimates.map((q) => {
            const sc = STATUS_COLORS[q.status] || STATUS_COLORS.Draft;
            return (
              <div key={q.id} className="bk-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`estimate-${q.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{q.number}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: sc.b, color: sc.c }} data-testid={`estimate-status-${q.id}`}>{q.status}</span>
                  </div>
                  <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>{q.client_name} · {money(q.total)}{q.expiry_date ? ` · expires ${q.expiry_date}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} data-testid={`estimate-status-select-${q.id}`} disabled={q.status === "Converted"}
                      className="appearance-none text-sm border rounded-md pl-3 pr-7 py-2 outline-none bg-white disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                      {statuses.filter((s) => s !== "Converted").map((s) => <option key={s} value={s}>{s}</option>)}
                      {q.status === "Converted" && <option value="Converted">Converted</option>}
                    </select>
                    <CaretDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--bk-text-2)" />
                  </div>
                  {q.status !== "Converted" ? (
                    <button onClick={() => convert(q.id)} data-testid={`convert-estimate-${q.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border transition-colors hover:bg-white" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}>
                      Convert to invoice <ArrowRight size={14} weight="bold" />
                    </button>
                  ) : (
                    <button onClick={() => navigate(`/business/${businessId}?tab=invoices`)} className="text-sm font-semibold" style={{ color: "var(--bk-accent)" }}>View invoice</button>
                  )}
                  <button onClick={() => del(q.id)} className="p-2 rounded-md hover:bg-[#FDECE8] transition-colors"><Trash size={16} color="#B23B1F" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
